// Pure helpers for file uploads and downloads (unit-tested in files.test.ts).

export const MAX_OVERRIDE_REASON = 500

/** Lower-cased extension including the dot ('' when there is none). */
export function fileExtension(name: string): string {
    const i = name.lastIndexOf('.')
    return i > 0 && i < name.length - 1 ? name.slice(i).toLowerCase() : ''
}

export function isAllowedExtension(name: string, allowed: string[]): boolean {
    const ext = fileExtension(name)
    return ext !== '' && allowed.some((a) => a.toLowerCase() === ext)
}

export function formatSize(bytes: number): string {
    if (!Number.isFinite(bytes) || bytes < 0) return ''
    if (bytes < 1024) return `${bytes} B`
    const units = ['KB', 'MB', 'GB']
    let v = bytes / 1024
    let u = 0
    while (v >= 1024 && u < units.length - 1) { v /= 1024; u++ }
    return `${v.toFixed(1).replace(/\.0$/, '')} ${units[u]}`
}

export interface ReasonCheck {
    ok: boolean
    length: number
    /** Characters still needed to reach the minimum (0 when satisfied). */
    remaining: number
}

export function validateOverrideReason(reason: string, minLen: number): ReasonCheck {
    const length = reason.trim().length
    const remaining = Math.max(0, minLen - length)
    return { ok: remaining === 0 && length <= MAX_OVERRIDE_REASON, length, remaining }
}

/** Filename from a Content-Disposition header (RFC 5987 `filename*` wins). */
export function parseContentDispositionFilename(header: string | null | undefined, fallback = 'download'): string {
    if (!header) return fallback
    const star = /filename\*\s*=\s*([^;]+)/i.exec(header)
    if (star) {
        const m = /^\s*[^']*'[^']*'(.*)$/.exec(star[1].trim())
        const raw = m ? m[1] : star[1].trim()
        try { return decodeURIComponent(raw) || fallback } catch { /* fall through */ }
    }
    const plain = /filename\s*=\s*(?:"((?:[^"\\]|\\.)*)"|([^;]+))/i.exec(header)
    if (plain) {
        const v = plain[1] !== undefined ? plain[1].replace(/\\(.)/g, '$1') : plain[2].trim()
        return v || fallback
    }
    return fallback
}

/** Save a blob through a temporary <a download>. */
export function saveBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
}
